/**
 * SME Portal — Dashboard page.
 * Overview of pending validations, draft reports, feedback, and delivery tracking.
 * Shows specialization-specific content based on the user's SME domain role.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Grid, Paper, Box, CircularProgress, Alert, Chip,
} from '@mui/material';
import { CheckCircle, Description, Feedback, LocalShipping, PlayCircle } from '@mui/icons-material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { authFetch } from '../../../api/authFetch';
import PortalTooltip from '../../../components/PortalTooltip';
import RefreshButton from '../../../components/RefreshButton';

interface SMEOutletContext {
  userRole?: string | null;
  roleDisplayName?: string | null;
}

const SMEDashboard: React.FC = () => {
  const { roleDisplayName } = (useOutletContext<SMEOutletContext>() || {});
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const navigate = useNavigate();

  // Derive the specialization label (e.g., "Cloud Security Adoption")
  const specialization = roleDisplayName
    ? roleDisplayName.replace(/^SME\s*[–—-]\s*/, '')
    : null;

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const safeFetch = (url: string) =>
      authFetch(url).then(r => r.json()).catch(() => ({}));

    Promise.all([
      safeFetch('/api/sme/validations?status=pending'),
      safeFetch('/api/sme/reports/drafts'),
      safeFetch('/api/sme/playback-feedback'),
      safeFetch('/api/sme/reports/delivery-status'),
      safeFetch('/api/sme/playback-sessions'),
    ])
      .then(([validations, drafts, feedback, delivery, sessions]) => {
        setStats({
          pendingValidations: validations.validations?.length || 0,
          draftReports: drafts.reports?.length || 0,
          pendingFeedback: feedback.feedback?.length || 0,
          activeDeliveries: delivery.reports?.filter((r: any) => !r.is_expired)?.length || 0,
          playbackSessions: sessions.sessions?.length || 0,
        });
        setLastRefreshed(new Date());
      })
      .catch(() => setError('Failed to load dashboard data'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const statCards = [
    { label: 'Pending Validations', value: stats?.pendingValidations || 0, icon: <CheckCircle fontSize="large" />, color: '#483698', path: '/sme/validations', tooltipKey: 'sme.dashboard.pending_validations' },
    { label: 'Draft Reports', value: stats?.draftReports || 0, icon: <Description fontSize="large" />, color: '#6B4FB0', path: '/sme/drafts', tooltipKey: 'sme.dashboard.draft_reports' },
    { label: 'Playback Sessions', value: stats?.playbackSessions || 0, icon: <PlayCircle fontSize="large" />, color: '#005EB8', path: '/sme/playback', tooltipKey: 'sme.dashboard.playback_sessions' },
    { label: 'Pending Feedback', value: stats?.pendingFeedback || 0, icon: <Feedback fontSize="large" />, color: '#8B6FCE', path: '/sme/playback', tooltipKey: 'sme.dashboard.pending_feedback' },
    { label: 'Active Deliveries', value: stats?.activeDeliveries || 0, icon: <LocalShipping fontSize="large" />, color: '#A48FE0', path: '/sme/delivery', tooltipKey: 'sme.dashboard.active_deliveries' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          KPMG SME Dashboard
        </Typography>
        {specialization && (
          <Chip
            label={specialization}
            sx={{
              backgroundColor: '#483698',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          />
        )}
        <Box sx={{ ml: 'auto' }}>
          <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <PortalTooltip tooltipKey={card.tooltipKey} placement="top">
              <Paper
                elevation={2}
                onClick={() => card.path && navigate(card.path)}
                sx={{
                  p: 3, textAlign: 'center',
                  cursor: card.path ? 'pointer' : 'default',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': card.path ? { transform: 'translateY(-4px)', boxShadow: 6 } : {},
                }}
              >
              <Box sx={{ color: card.color, mb: 1 }}>{card.icon}</Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: card.color }}>
                {card.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.label}
              </Typography>
              {card.path && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Click to view →
                </Typography>
              )}
            </Paper>
            </PortalTooltip>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SMEDashboard;
